import { useCallback, useMemo, useRef, useState } from 'react'
import type { RightPanelRequest, RightPanelValue } from './rightPanelContext'

type FoldState = {
  expanded: boolean
  unfoldedModules: ReadonlySet<string>
}

const FOLDED: FoldState = { expanded: false, unfoldedModules: new Set() }

const useRightPanelState = () => {
  const [{ expanded, unfoldedModules }, setFoldState] = useState<FoldState>(FOLDED)
  const [request, setRequest] = useState<RightPanelRequest>(null)
  const [notifications, setNotifications] = useState<Record<string, number>>({})
  const requestCounter = useRef(0)

  const toggle = useCallback(
    () => setFoldState(current => (current.expanded ? FOLDED : { ...current, expanded: true })),
    [],
  )

  const toggleModule = useCallback((key: string) => {
    setFoldState(current => {
      const next = new Set(current.unfoldedModules)
      if (!next.delete(key)) next.add(key)
      return { ...current, unfoldedModules: next }
    })
  }, [])

  const openModule = useCallback((key: string) => {
    setFoldState(current => ({ expanded: true, unfoldedModules: new Set(current.unfoldedModules).add(key) }))
    requestCounter.current += 1
    setRequest({ key, id: requestCounter.current })
  }, [])

  const setNotification = useCallback((key: string, count: number) => {
    setNotifications(previous => (previous[key] === count ? previous : { ...previous, [key]: count }))
  }, [])

  const panel: RightPanelValue = useMemo(
    () => ({ expanded, toggle, openModule, request, unfoldedModules, toggleModule, setNotification }),
    [expanded, toggle, openModule, request, unfoldedModules, toggleModule, setNotification],
  )

  return { panel, notifications }
}

export default useRightPanelState
